import * as React from "react";
import { FC } from "react";
import makeStyles from "@mui/styles/makeStyles";

interface SkeletonRowProps {
   /** Number of columns to render skeleton cells for */
   columnCount: number;
   /** CSS grid-template-columns value */
   gridTemplateColumns: string;
   /** Optional fixed height for the row */
   itemHeight?: number;
}

/**
 * Skeleton row component with shimmer animation for loading states
 */
export const SkeletonRow: FC<SkeletonRowProps> = ({ columnCount, gridTemplateColumns, itemHeight }) => {
   const classes = useStyles();

   return (
      <div
         className={classes.row}
         style={{
            gridTemplateColumns,
            height: itemHeight,
            minHeight: itemHeight,
            maxHeight: itemHeight,
         }}
      >
         {Array.from({ length: columnCount }).map((_, i) => (
            <div key={`skeleton-${i}`} className={classes.cell}>
               <div className={classes.skeleton} />
            </div>
         ))}
      </div>
   );
};

const useStyles = makeStyles({
   row: {
      display: 'grid',
      // Use box-shadow instead of border to avoid browser subpixel rendering glitch
      // See: https://github.com/petyosi/react-virtuoso/issues/1042
      boxShadow: 'inset 0 1px 0 0 #e0e0e0',
      boxSizing: 'border-box',
      overflow: 'hidden',
   },
   cell: {
      padding: '8px 4px',
      display: 'flex',
      alignItems: 'center',
      overflow: 'hidden',
   },
   skeleton: {
      height: '20px',
      width: '100%',
      backgroundColor: '#e8e8e8',
      borderRadius: '4px',
      position: 'relative',
      overflow: 'hidden',
      '&::after': {
         content: '""',
         position: 'absolute',
         top: 0,
         left: 0,
         right: 0,
         bottom: 0,
         background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
         animation: '$shimmer 1.5s infinite',
      },
   },
   '@keyframes shimmer': {
      '0%': {
         transform: 'translateX(-100%)',
      },
      '100%': {
         transform: 'translateX(100%)',
      },
   },
});
